import * as React from "react";
import { Button, Grid, TextField } from "@mui/material";
import { Formik } from "formik";
import * as Yup from "yup";
import { postNotification } from "../../utils/Notifications";
import { redirectUrlAuth, urlParams } from "../../utils/urlParams";
import { getData } from "../../utils/APIUtilities";
import { getEnvironmentVariable } from "../../global/environmentVariables";
import { utf8ToBase64 } from "../../utils/GeneralUtils";

const { client_id, redirect_uri, state, response_type } = urlParams();
const storedClientId = sessionStorage?.getItem("client_id");

const HOME_PAGE = getEnvironmentVariable("HOME_PAGE");
const AUTH_CLIENT_ID = getEnvironmentVariable("AUTH_CLIENT_ID") || "web-app";

export async function signIn(userCredentials, setProcessing) {
  const { username, password } = userCredentials;

  try {
    setProcessing(true);

    if (!redirect_uri && !HOME_PAGE) throw new Error("Missing redirect_uri");
    if (!username) throw new Error("Missing email");
    if (!password) throw new Error("Missing password");

    const _state = state || localStorage?.getItem("state");

    if (response_type === "code") {
      const params = {
        response_type: response_type,
        redirect_uri: redirect_uri || HOME_PAGE,
        state: _state,
      };
      const headers = {
        "client-id": client_id || storedClientId || AUTH_CLIENT_ID,
        Authorization: `Basic ${utf8ToBase64(`${username}:${password}`)}`,
      };
      const customUrl = getEnvironmentVariable("API_URL").replace("/v1", "");

      const userSignin = await getData(
        `/oauth/authorize`,
        params,
        headers,
        customUrl,
      );

      await redirectUrlAuth({
        cognitoProps: {
          ...userSignin,
        },
        redirect_uri: redirect_uri,
      });
    } else {
      throw new Error("Invalid response_type");
    }

    return;
  } catch (err) {
    postNotification("error", err);
    setProcessing(false);
  }
}

export default function CognitoSignInForm({
  formState,
  updateFormState,
  setProcessing,
}) {
  const onSubmit = async (values) => {
    updateFormState(values);
    await signIn(values, setProcessing);
  };

  return (
    <Formik
      initialValues={{
        username: formState?.username || formState?.email || "",
        password: formState?.password || "",
      }}
      enableReinitialize
      validationSchema={Yup.object().shape({
        username: Yup.string().max(128).required("Required"),
        password: Yup.string().max(255).required("Required"),
      })}
      onSubmit={onSubmit.bind(this)}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        setFieldValue,
        touched,
        values,
      }) => (
        <form noValidate onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                error={Boolean(touched.username && errors.username)}
                helperText={touched.username && errors.username}
                autoComplete="username"
                name="username"
                value={values.username}
                label={"Email"}
                variant="outlined"
                required
                fullWidth
                onBlur={handleBlur}
                onChange={(e) => {
                  const trimmedString = e.target.value.trim().toLowerCase();
                  setFieldValue("username", trimmedString);
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                error={Boolean(touched.password && errors.password)}
                helperText={touched.password && errors.password}
                autoComplete="password"
                name="password"
                value={values.password}
                type="password"
                label="Password"
                variant="outlined"
                required
                fullWidth
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12}>
              <Button type="submit" fullWidth variant="contained">
                Sign In
              </Button>
            </Grid>
          </Grid>
        </form>
      )}
    </Formik>
  );
}
